import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ProfileSection } from "@/lib/api/profile-sections";
import {
  useCreateProfileSection,
  useCreateProfileSectionWithImage,
  useDeleteProfileSection,
  useProfileSections,
  useReorderProfileSections,
  useUpdateProfileSection,
  useUpdateProfileSectionImage,
  useRemoveProfileSectionImage,
} from "@/lib/hooks/profile-sections/use-profile-sections";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AlertCircle, Plus } from "lucide-react";
import { useState } from "react";
import { ProfileSectionForm } from "./profile-section-form";
import { ProfileSectionItem } from "./profile-section-item";

interface ProfileSectionsManagerProps {
  userId: string;
}

// Skeleton loading component that matches the actual ProfileSectionItem layout
function SectionSkeleton() {
  return (
    <Card className="p-4">
      <div className="flex gap-3">
        {/* Drag handle skeleton */}
        <Skeleton className="h-5 w-5 flex-shrink-0" />

        <div className="flex-1 space-y-3">
          {/* Title skeleton */}
          <Skeleton className="h-6 w-48" />

          {/* Description skeleton - collapsed state */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>

        {/* Action buttons skeleton */}
        <div className="flex gap-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-16" />
        </div>
      </div>
    </Card>
  );
}

export function ProfileSectionsManager({
  userId,
}: ProfileSectionsManagerProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingSection, setEditingSection] = useState<ProfileSection | null>(
    null,
  );

  const {
    data: sections = [],
    isLoading,
    error,
    refetch,
  } = useProfileSections(userId);
  const createSection = useCreateProfileSection(userId);
  const createSectionWithImage = useCreateProfileSectionWithImage(userId);
  const updateSection = useUpdateProfileSection(userId);
  const updateSectionImage = useUpdateProfileSectionImage(userId);
  const removeImage = useRemoveProfileSectionImage(userId);
  const deleteSection = useDeleteProfileSection(userId);
  const reorderSections = useReorderProfileSections(userId);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex((section) => section.id === active.id);
    const newIndex = sections.findIndex((section) => section.id === over.id);

    const newSections = arrayMove(sections, oldIndex, newIndex);
    reorderSections.mutate(newSections.map((section) => section.id));
  };

  const handleFormSubmit = async (data: {
    title: string;
    description: string;
    file?: File;
    removeImage?: boolean;
  }) => {
    try {
      // Check section limit for new sections
      if (!editingSection && sections.length >= 10) {
        throw new Error("Maximum of 10 profile sections allowed per mentor");
      }

      if (editingSection) {
        // Update existing section
        await updateSection.mutateAsync({
          sectionId: editingSection.id,
          title: data.title,
          description: data.description,
        });

        if (data.removeImage) {
          await removeImage.mutateAsync(editingSection.id);
        } else if (data.file) {
          await updateSectionImage.mutateAsync({
            sectionId: editingSection.id,
            file: data.file,
          });
        }
      } else {
        // Create new section
        if (data.file) {
          await createSectionWithImage.mutateAsync({
            title: data.title,
            description: data.description,
            file: data.file,
          });
        } else {
          await createSection.mutateAsync({
            title: data.title,
            description: data.description,
          });
        }
      }

      setShowForm(false);
      setEditingSection(null);
    } catch (error) {
      console.error("Error saving section:", error);
      // The error will be displayed by the form component
      throw error;
    }
  };

  const handleEdit = (section: ProfileSection) => {
    setEditingSection(section);
    setShowForm(true);
  };

  const handleDelete = (sectionId: string) => {
    deleteSection.mutate(sectionId);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSection(null);
  };

  const isFormLoading =
    createSection.isPending ||
    createSectionWithImage.isPending ||
    updateSection.isPending ||
    updateSectionImage.isPending ||
    removeImage.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Custom Profile Sections</h3>
          <p className="text-muted-foreground text-sm">
            Add custom sections to showcase more about this mentor (
            {sections.length}/10)
          </p>
        </div>
        {!showForm && sections.length < 10 && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Section
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <ProfileSectionForm
          section={editingSection || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleCancel}
          isLoading={isFormLoading}
        />
      )}

      {/* Error State */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/5 p-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <div>
              <h4 className="font-medium">Failed to load profile sections</h4>
              <p className="mt-1 text-sm">
                There was an error loading the sections. Please try again.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="mt-2"
              >
                Retry
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Sections List */}
      {isLoading ? (
        <div className="space-y-4">
          <SectionSkeleton />
          <SectionSkeleton />
          <SectionSkeleton />
        </div>
      ) : sections.length === 0 && !error ? (
        <Card className="p-8 text-center">
          <div className="text-muted-foreground">
            <Plus className="mx-auto mb-4 h-12 w-12 opacity-50" />
            <h4 className="mb-2 text-lg font-medium">No custom sections yet</h4>
            <p className="mb-4 text-sm">
              Add custom sections to help teens learn more about this mentor
            </p>
            {!showForm && (
              <Button onClick={() => setShowForm(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Section
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sections.map((section) => section.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {sections.map((section) => (
                <ProfileSectionItem
                  key={section.id}
                  section={section}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isDeleting={deleteSection.isPending}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
